import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { COLLS, PB_RECORDS, LEADS, DEMO_HUB, NUDGE_ALERTS } from '../constants';
import { authApi, leadsApi, collateralsApi, playbooksApi, nudgesApi, tokenStore,
  type LoginResponse, type LeadData, type CollateralData, type PlaybookData, type NudgeData } from '../api/client';
import type { DemoHub, User, Screen } from '../types';

const USE_API = import.meta.env.VITE_USE_API === 'true';

interface AppContextValue {
  currentUser: User | null;
  screen: Screen;
  go: (screen: Screen) => void;
  login: (user: User) => void;
  loginWithCredentials: (email: string, password: string) => Promise<void>;
  logout: () => void;
  // Data — unified type (local or API-sourced)
  colls: CollateralData[];
  setColls: React.Dispatch<React.SetStateAction<CollateralData[]>>;
  playbooks: PlaybookData[];
  setPlaybooks: React.Dispatch<React.SetStateAction<PlaybookData[]>>;
  leads: LeadData[];
  addLead: (lead: Partial<LeadData>) => Promise<void>;
  updateLead: (id: string, updates: Partial<LeadData>) => Promise<void>;
  deleteLead: (id: string) => Promise<void>;
  demoHub: DemoHub;
  setDemoHub: React.Dispatch<React.SetStateAction<DemoHub>>;
  nudges: NudgeData[];
  setNudges: React.Dispatch<React.SetStateAction<NudgeData[]>>;
  // State
  libraryTab: 'collaterals' | 'playbooks';
  setLibraryTab: (tab: 'collaterals' | 'playbooks') => void;
  activePbIdx: number;
  setActivePbIdx: (idx: number) => void;
  isLoading: boolean;
  // Toast
  toast: (msg: string, type?: 'success' | 'error' | 'info') => void;
  toastMsg: string;
  toastVisible: boolean;
  toastType: 'success' | 'error' | 'info';
}

const AppContext = createContext<AppContextValue | null>(null);

// ── Local mock data adapters (keep the app working without a backend) ──
const mockColls: CollateralData[] = COLLS.map(c => ({
  _id: String(c.id),
  id: undefined,
  name: c.name, type: c.type, size: c.size, pages: c.pages, tags: c.tags,
  sharedBy: c.sharedBy, sharedByIni: c.sharedByIni, sharedDate: c.sharedDate,
  recipients: c.recipients, pdata: c.pdata,
  url: c.url, demoLogin: c.demoLogin, demoNote: c.demoNote,
}));

const mockPlaybooks: PlaybookData[] = PB_RECORDS.map((pb, i) => ({
  ...pb, _id: String(i),
  landingPage: { ...pb.landingPage, heroStyle: pb.landingPage.heroStyle as 'gradient' | 'minimal' },
  security: { ...pb.security, expiry: pb.security.expiry ?? null },
})) as unknown as PlaybookData[];

const mockLeads: LeadData[] = LEADS.map(l => ({ ...l, _id: l.id }));
const mockNudges: NudgeData[] = NUDGE_ALERTS.map(n => ({ ...n, _id: String(n.id) }));

const MOCK_USER_KEY = 'salesbook_mock_user';

function loadStoredUser(): User | null {
  if (USE_API) return null;
  try { return JSON.parse(localStorage.getItem(MOCK_USER_KEY) || 'null'); } catch { return null; }
}

export function AppProvider({ children }: { children: React.ReactNode }) {
  const storedUser = loadStoredUser();
  const [currentUser, setCurrentUser] = useState<User | null>(storedUser);
  const [screen, setScreen] = useState<Screen>(storedUser ? 'home' : 'login');
  const [colls, setColls] = useState<CollateralData[]>(mockColls);
  const [playbooks, setPlaybooks] = useState<PlaybookData[]>(mockPlaybooks);
  const [leads, setLeads] = useState<LeadData[]>(mockLeads);
  const [demoHub, setDemoHub] = useState<DemoHub>(DEMO_HUB);
  const [nudges, setNudges] = useState<NudgeData[]>(mockNudges);
  const [libraryTab, setLibraryTab] = useState<'collaterals' | 'playbooks'>('collaterals');
  const [activePbIdx, setActivePbIdx] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [toastMsg, setToastMsg] = useState('');
  const [toastVisible, setToastVisible] = useState(false);
  const [toastType, setToastType] = useState<'success' | 'error' | 'info'>('success');

  const toast = useCallback((msg: string, type: 'success' | 'error' | 'info' = 'success') => {
    setToastMsg(msg); setToastType(type); setToastVisible(true);
    setTimeout(() => setToastVisible(false), 2800);
  }, []);

  const go = useCallback((s: Screen) => setScreen(s), []);

  // ── Load data from API ──────────────────────────────────────
  const loadApiData = useCallback(async () => {
    if (!USE_API) return;
    try {
      setIsLoading(true);
      const [collRes, leadRes, pbRes, nudgeRes] = await Promise.allSettled([
        collateralsApi.list({ limit: '100' }),
        leadsApi.list({ limit: '100' }),
        playbooksApi.list({ limit: '100' }),
        nudgesApi.list(),
      ]);
      if (collRes.status === 'fulfilled' && collRes.value.data) setColls(collRes.value.data);
      if (leadRes.status === 'fulfilled' && leadRes.value.data) setLeads(leadRes.value.data);
      if (pbRes.status   === 'fulfilled' && pbRes.value.data)   setPlaybooks(pbRes.value.data);
      if (nudgeRes.status === 'fulfilled')                       setNudges(nudgeRes.value as NudgeData[]);
    } catch {
      toast('Using offline data — backend not connected', 'info');
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  // ── Re-check stored token on mount ──────────────────────────
  useEffect(() => {
    const token = tokenStore.get();
    if (token && USE_API) {
      authApi.me().then(user => {
        setCurrentUser({ name: user.name, email: user.email, role: user.role as User['role'], title: user.title, ini: user.ini, color: user.color, pwd: '' });
        setScreen('home');
        loadApiData();
      }).catch(() => tokenStore.remove());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Auth logout event from API interceptor ───────────────────
  useEffect(() => {
    const handler = () => { setCurrentUser(null); setScreen('login'); };
    window.addEventListener('auth:logout', handler);
    return () => window.removeEventListener('auth:logout', handler);
  }, []);

  // ── Login: try API first, fall back to mock accounts ─────────
  const loginWithCredentials = useCallback(async (email: string, password: string) => {
    if (USE_API) {
      const res: LoginResponse = await authApi.login(email, password);
      tokenStore.set(res.token);
      const u = res.user;
      setCurrentUser({ name: u.name, email: u.email, role: u.role as User['role'], title: u.title, ini: u.ini, color: u.color, pwd: '' });
      setScreen('home');
      await loadApiData();
    } else {
      throw new Error('USE_API is false — use mock login');
    }
  }, [loadApiData]);

  const login = useCallback((user: User) => {
    if (!USE_API) localStorage.setItem(MOCK_USER_KEY, JSON.stringify(user));
    setCurrentUser(user);
    setScreen('home');
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(MOCK_USER_KEY);
    tokenStore.remove();
    setCurrentUser(null);
    setScreen('login');
    setColls(mockColls);
    setLeads(mockLeads);
    setPlaybooks(mockPlaybooks);
    setNudges(mockNudges);
  }, []);

  // ── Lead mutations ────────────────────────────────────────────
  const addLead = useCallback(async (lead: Partial<LeadData>) => {
    if (USE_API) {
      const created = await leadsApi.create(lead);
      setLeads(prev => [created, ...prev]);
    } else {
      const local: LeadData = { _id: `local-${Date.now()}`, name: '', ini: '', org: '', email: '', phone: '', heat: 'cold', intent: 0, lastSeen: 'Just now', firstSeen: 'Today', sourceType: 'manual', source: 'Added manually', signals: [], sessions: 0, totalTime: '—', status: 'new', ...lead };
      setLeads(prev => [local, ...prev]);
    }
  }, []);

  const updateLead = useCallback(async (id: string, updates: Partial<LeadData>) => {
    if (USE_API) {
      const updated = await leadsApi.update(id, updates);
      setLeads(prev => prev.map(l => l._id === id ? updated : l));
    } else {
      setLeads(prev => prev.map(l => l._id === id ? { ...l, ...updates } : l));
    }
  }, []);

  const deleteLead = useCallback(async (id: string) => {
    if (USE_API) await leadsApi.delete(id);
    setLeads(prev => prev.filter(l => l._id !== id));
  }, []);

  return (
    <AppContext.Provider value={{
      currentUser, screen, go, login, loginWithCredentials, logout,
      colls, setColls, playbooks, setPlaybooks,
      leads, addLead, updateLead, deleteLead,
      demoHub, setDemoHub, nudges, setNudges,
      libraryTab, setLibraryTab,
      activePbIdx, setActivePbIdx, isLoading,
      toast, toastMsg, toastVisible, toastType,
    }}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp(): AppContextValue {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
}
